import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, TrendingUp, AlertCircle, RefreshCw, Download, Search,
  Filter, ChevronLeft, ChevronRight, X, ExternalLink, CheckCircle2,
  XCircle, Clock, ArrowUpLeft, AlertTriangle, ShieldOff, Info, RotateCcw,
  BarChart2, ShieldCheck, Loader2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (pence, currency = 'gbp') => {
  const sym = { gbp: '£', usd: '$', eur: '€' }[currency?.toLowerCase()] ?? (currency?.toUpperCase() + ' ');
  return `${sym}${(pence / 100).toFixed(2)}`;
};

const relTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ status }) => {
  const cfg = {
    succeeded: { cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2, label: 'Succeeded' },
    failed: { cls: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle, label: 'Failed' },
    refunded: { cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: ArrowUpLeft, label: 'Refunded' },
    partial_refund: { cls: 'bg-amber-500/10 text-amber-300 border-amber-500/20', icon: ArrowUpLeft, label: 'Part-refund' },
    disputed: { cls: 'bg-red-500/10 text-red-300 border-red-500/20', icon: AlertTriangle, label: 'Disputed' },
    pending: { cls: 'bg-cream/[0.05] text-cream/40 border-gold/10', icon: Clock, label: 'Pending' },
  };
  const c = cfg[status] || cfg['pending'];
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${c.cls}`}>
      <Icon size={9} />
      {c.label}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const cfg = {
    payment_link: { cls: 'bg-sky-500/10 text-sky-400', label: 'Payment Link' },
    checkout: { cls: 'bg-purple-500/10 text-purple-400', label: 'Checkout' },
    direct: { cls: 'bg-cream/[0.05] text-cream/30', label: 'Direct' },
  };
  const c = cfg[source] || cfg['direct'];
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded ${c.cls}`}>{c.label}</span>
  );
};

const SummaryCard = ({ label, value, sub, icon: Icon, color, tooltip }) => (
  <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon size={14} className={color} />
      {tooltip && <Info size={10} className="text-cream/15" title={tooltip} />}
    </div>
    <p className="text-cream text-2xl font-semibold tabular-nums">{value}</p>
    <p className="text-cream/25 text-xs mt-0.5">{label}</p>
    {sub && <p className="text-cream/15 text-[10px] mt-0.5">{sub}</p>}
  </div>
);

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: '90d', label: '90 days' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
];

const STATUSES = [
  { value: 'all', label: 'All statuses' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'partial_refund', label: 'Part-refunded' },
  { value: 'disputed', label: 'Disputed' },
];

const SOURCES = [
  { value: 'all', label: 'All sources' },
  { value: 'payment_link', label: 'Payment Link' },
  { value: 'checkout', label: 'Checkout' },
  { value: 'direct', label: 'Direct' },
];

const RefundModal = ({ txn, token, onClose, onSuccess }) => {
  const [partialAmount, setPartialAmount] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const maxRefund = (txn.amount - (txn.amount_refunded || 0)) / 100;
  const isFullRefund = !partialAmount || Number(partialAmount) >= maxRefund;

  const handleRefund = async () => {
    setLoading(true);
    setError(null);
    const body = { reason };
    if (partialAmount && Number(partialAmount) < maxRefund) {
      body.amount_pence = Math.round(Number(partialAmount) * 100);
    }
    try {
      const res = await fetch(`${API_URL}/api/cms/payments/${txn.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Refund failed');
      onSuccess(d);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="refund-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#06111f] border border-gold/[0.12] rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-cream font-medium text-sm">Issue Refund</h2>
            <p className="text-cream/30 text-xs mt-0.5">{txn.customer_email} · {fmt(txn.amount, txn.currency)}</p>
          </div>
          <button onClick={onClose} className="text-cream/30 hover:text-cream/60 p-1">
            <X size={16} />
          </button>
        </div>

        {/* Amount */}
        <div className="mb-4">
          <label className="block text-cream/40 text-[10px] uppercase tracking-wide mb-1.5">Amount to refund</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/30 text-xs">{txn.currency?.toUpperCase() === 'GBP' ? '£' : txn.currency?.toUpperCase()}</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={maxRefund}
              value={partialAmount}
              onChange={e => setPartialAmount(e.target.value)}
              placeholder={`${maxRefund.toFixed(2)} (full refund)`}
              data-testid="refund-amount-input"
              className="w-full bg-navy border border-gold/[0.06] text-cream/70 text-sm pl-7 pr-3 py-2.5 rounded-xl focus:outline-none focus:border-gold/20 placeholder-cream/15"
            />
          </div>
          <p className="text-cream/20 text-[10px] mt-1">
            Leave blank for full refund of {fmt(txn.amount - (txn.amount_refunded || 0), txn.currency)}
          </p>
        </div>

        {/* Reason */}
        <div className="mb-5">
          <label className="block text-cream/40 text-[10px] uppercase tracking-wide mb-1.5">Reason</label>
          <select
            value={reason}
            onChange={e => setReason(e.target.value)}
            data-testid="refund-reason-select"
            className="w-full bg-navy border border-gold/[0.06] text-cream/60 text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-gold/20"
          >
            <option value="requested_by_customer">Requested by customer</option>
            <option value="duplicate">Duplicate charge</option>
            <option value="fraudulent">Fraudulent</option>
          </select>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-xs">{error}</div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gold/[0.06] text-cream/40 hover:text-cream/60 text-xs transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleRefund}
            disabled={loading}
            data-testid="refund-confirm-btn"
            className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/15 text-xs transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            <RotateCcw size={12} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Processing…' : (isFullRefund ? `Refund ${fmt(txn.amount - (txn.amount_refunded || 0), txn.currency)}` : `Refund ${fmt(Math.round(Number(partialAmount) * 100), txn.currency)}`)}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailPanel = ({ txn, token, onClose, onRefundSuccess }) => {
  const [showRefund, setShowRefund] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [reinstated, setReinstated] = useState(txn?.access_reinstated || false);
  if (!txn) return null;
  const canRefund = txn.status === 'succeeded' && !txn.refunded;
  const canReinstate = (txn.status === 'refunded' || txn.status === 'partial_refund') && !reinstated;

  const handleReinstate = async () => {
    if (!window.confirm(`Reinstate course access for ${txn.customer_email}?`)) return;
    setReinstating(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/payments/${txn.id}/reinstate-access`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, credentials: 'include',
      });
      if (res.ok) { setReinstated(true); onRefundSuccess?.(); }
    } finally { setReinstating(false); }
  };
  const Row = ({ label, value, mono }) => value ? (
    <div className="flex items-start gap-3 py-2 border-b border-gold/[0.04] last:border-0">
      <span className="text-cream/25 text-[10px] w-36 flex-shrink-0 pt-0.5 uppercase tracking-wide">{label}</span>
      <span className={`text-cream/60 text-xs break-all ${mono ? 'font-mono text-[10px]' : ''}`}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="payment-detail-panel">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#06111f] border-l border-gold/[0.08] overflow-y-auto">
        <div className="sticky top-0 bg-[#06111f] border-b border-gold/[0.06] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-cream font-medium text-sm">{fmt(txn.amount, txn.currency)}</p>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={txn.status} />
              <SourceBadge source={txn.source} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canRefund && (
              <button
                onClick={() => setShowRefund(true)}
                data-testid="refund-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 text-amber-400 hover:bg-amber-500/[0.12] text-xs transition-all"
              >
                <RotateCcw size={11} />
                Refund
              </button>
            )}
            {canReinstate && (
              <button
                onClick={handleReinstate}
                disabled={reinstating}
                data-testid="reinstate-access-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/[0.08] border border-teal-500/20 text-teal-400 hover:bg-teal-500/[0.12] text-xs transition-all disabled:opacity-50"
              >
                {reinstating ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                Reinstate Access
              </button>
            )}
            {reinstated && (
              <span data-testid="access-reinstated-badge" className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-teal-500/[0.08] border border-teal-500/20 text-teal-400 text-xs">
                <ShieldCheck size={11} /> Access Reinstated
              </span>
            )}
            <button onClick={onClose} className="text-cream/30 hover:text-cream/60 transition-colors p-1">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Customer */}
          <section>
            <h3 className="text-cream/30 text-[10px] uppercase tracking-wider mb-2">Customer</h3>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <Row label="Name" value={txn.customer_name || '—'} />
              <Row label="Email" value={txn.customer_email || '—'} />
              {txn.stripe_customer_id && <Row label="Stripe Customer" value={txn.stripe_customer_id} mono />}
            </div>
          </section>

          {/* Purchase */}
          <section>
            <h3 className="text-cream/30 text-[10px] uppercase tracking-wider mb-2">Purchase</h3>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <Row label="Product" value={txn.product_name || '—'} />
              <Row label="Quantity" value={txn.quantity?.toString()} />
              <Row label="Amount" value={fmt(txn.amount, txn.currency)} />
              {txn.amount_refunded > 0 && <Row label="Refunded" value={fmt(txn.amount_refunded, txn.currency)} />}
              <Row label="Currency" value={txn.currency?.toUpperCase()} />
              <Row label="Payment method" value={txn.payment_method_brand ? `${txn.payment_method_brand} ···· ${txn.payment_method_last4}` : txn.payment_method_type} />
            </div>
          </section>

          {/* Acquisition */}
          {(txn.success_url || txn.referrer || txn.metadata?.utm_source) && (
            <section>
              <h3 className="text-cream/30 text-[10px] uppercase tracking-wider mb-2">Acquisition</h3>
              <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
                <Row label="Success URL" value={txn.success_url} />
                <Row label="Cancel URL" value={txn.cancel_url} />
                <Row label="Referrer" value={txn.referrer} />
                {txn.metadata?.utm_source && <Row label="UTM Source" value={txn.metadata.utm_source} />}
                {txn.metadata?.utm_campaign && <Row label="UTM Campaign" value={txn.metadata.utm_campaign} />}
              </div>
            </section>
          )}

          {/* Stripe IDs */}
          <section>
            <h3 className="text-cream/30 text-[10px] uppercase tracking-wider mb-2">Stripe Objects</h3>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <Row label="Charge ID" value={txn.stripe_charge_id} mono />
              <Row label="Payment Intent" value={txn.stripe_payment_intent_id} mono />
              <Row label="Checkout Session" value={txn.stripe_checkout_session_id} mono />
              <Row label="Payment Link" value={txn.stripe_payment_link_id} mono />
              <Row label="Product ID" value={txn.stripe_product_id} mono />
              <Row label="Price ID" value={txn.stripe_price_id} mono />
              <Row label="Invoice ID" value={txn.stripe_invoice_id} mono />
              {txn.receipt_url && (
                <div className="py-2 border-b border-gold/[0.04] last:border-0">
                  <a href={txn.receipt_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs text-gold/60 hover:text-gold transition-colors">
                    <ExternalLink size={10} />
                    View Stripe receipt
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* Metadata */}
          {Object.keys(txn.metadata || {}).length > 0 && (
            <section>
              <h3 className="text-cream/30 text-[10px] uppercase tracking-wider mb-2">Metadata</h3>
              <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
                {Object.entries(txn.metadata).map(([k, v]) => (
                  <Row key={k} label={k} value={String(v)} mono />
                ))}
              </div>
            </section>
          )}

          {/* Timeline */}
          {txn.timeline?.length > 0 && (
            <section>
              <h3 className="text-cream/30 text-[10px] uppercase tracking-wider mb-2">Timeline</h3>
              <div className="space-y-2">
                {txn.timeline.map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold/40 mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-cream/60 text-xs">{t.event}</p>
                      <p className="text-cream/20 text-[10px]">{relTime(t.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Attribution notice for payment link */}
          {txn.source === 'payment_link' && txn.success_url?.includes('stripe.com') && (
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4">
              <div className="flex gap-2">
                <Info size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-300/70 text-xs">
                  This payment was made via a direct Stripe Payment Link (buy.stripe.com).
                  No website page attribution is available because the customer did not
                  navigate from the website to this checkout.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {showRefund && (
        <RefundModal
          txn={txn}
          token={token}
          onClose={() => setShowRefund(false)}
          onSuccess={(result) => {
            setShowRefund(false);
            onRefundSuccess && onRefundSuccess(result);
            onClose();
          }}
        />
      )}
    </div>
  );
};

const AdminPayments = () => {
  const { token } = useAuth();
  const [period, setPeriod] = useState('30d');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        page: String(page),
        limit: '20',
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(sourceFilter !== 'all' && { source: sourceFilter }),
      });
      const [sumRes, txnRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/payments/summary?period=${period}`, { headers }),
        fetch(`${API_URL}/api/cms/payments?${params}`, { headers }),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (txnRes.ok) {
        const d = await txnRes.json();
        setTransactions(d.transactions || []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 1);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, page, search, statusFilter, sourceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch 30-day daily revenue for sparkline chart
  const fetchChart = useCallback(async () => {
    setChartLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/cms/payments/daily-revenue?days=30`, { headers });
      if (r.ok) { const d = await r.json(); if (d?.days) setDailyRevenue(d.days); }
    } catch {}
    setChartLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchChart(); }, [fetchChart]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [period, search, statusFilter, sourceFilter]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API_URL}/api/cms/payments/sync-stripe`, { headers });
      const d = await res.json();
      setSyncResult(d);
      await Promise.all([fetchData(), fetchChart()]);
    } catch (e) { setSyncResult({ error: String(e) }); }
    setSyncing(false);
  };

  const handleExport = () => {
    const params = new URLSearchParams({ period, ...(statusFilter !== 'all' && { status: statusFilter }), ...(search && { search }) });
    window.open(`${API_URL}/api/cms/payments/export.csv?${params}&token=${token}`, '_blank');
  };

  const periodLabel = PERIODS.find(p => p.key === period)?.label || period;

  return (
    <div data-testid="admin-payments">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Payments</h1>
          <p className="text-cream/30 text-sm mt-1">All Stripe transactions &amp; revenue</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="relative">
            <button
              onClick={() => setShowPeriodMenu(!showPeriodMenu)}
              data-testid="period-selector"
              className="flex items-center gap-2 bg-navy border border-gold/[0.08] text-cream/50 text-xs px-3 py-2 rounded-lg hover:border-gold/20 transition-colors"
            >
              <Filter size={12} />
              {periodLabel}
            </button>
            {showPeriodMenu && (
              <div className="absolute right-0 top-full mt-1 bg-[#0a1628] border border-gold/[0.1] rounded-xl shadow-xl z-20 py-1 w-40">
                {PERIODS.map(p => (
                  <button
                    key={p.key}
                    onClick={() => { setPeriod(p.key); setShowPeriodMenu(false); }}
                    className={`w-full text-left px-4 py-2 text-xs transition-colors ${period === p.key ? 'text-gold bg-gold/5' : 'text-cream/40 hover:text-cream/60 hover:bg-cream/[0.02]'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleExport}
            data-testid="export-csv-btn"
            className="flex items-center gap-1.5 bg-navy border border-gold/[0.08] text-cream/40 hover:text-cream/60 text-xs px-3 py-2 rounded-lg transition-colors"
          >
            <Download size={12} />
            CSV
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-stripe-btn"
            className="flex items-center gap-1.5 bg-gold/[0.08] border border-gold/20 text-gold text-xs px-3 py-2 rounded-lg hover:bg-gold/[0.12] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync from Stripe'}
          </button>
        </div>
      </div>

      {/* Refund success banner */}
      {refundSuccess && (
        <div className="mb-4 p-3 rounded-xl text-xs border bg-amber-500/10 border-amber-500/20 text-amber-300 flex items-center justify-between" data-testid="refund-success-banner">
          <span>Refund issued: {fmt(refundSuccess.amount_refunded, 'gbp')} · ID {refundSuccess.refund_id}</span>
          <button onClick={() => setRefundSuccess(null)} className="ml-2 opacity-50 hover:opacity-100"><X size={10} /></button>
        </div>
      )}

      {/* Sync result banner */}
      {syncResult && (
        <div className={`mb-4 p-3 rounded-xl text-xs border ${syncResult.error ? 'bg-red-500/10 border-red-500/20 text-red-300' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'}`}>
          {syncResult.error
            ? `Sync error: ${syncResult.error}`
            : `Sync complete — ${syncResult.charges_found} charges found, ${syncResult.created} new, ${syncResult.updated} updated`}
          <button onClick={() => setSyncResult(null)} className="ml-2 opacity-50 hover:opacity-100"><X size={10} /></button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <SummaryCard icon={TrendingUp}  color="text-emerald-400" label="Gross Revenue"   value={summary ? fmt(summary.gross_revenue, summary.currency) : '—'} sub={periodLabel} tooltip="Total of all successful payments before refunds" />
        <SummaryCard icon={CreditCard}  color="text-sky-400"     label="Net Revenue"     value={summary ? fmt(summary.net_revenue, summary.currency) : '—'} sub="after refunds" />
        <SummaryCard icon={CheckCircle2} color="text-green-400"  label="Payments"        value={summary?.count_succeeded ?? '—'} sub="successful" />
        <SummaryCard icon={ArrowUpLeft} color="text-amber-400"   label="Refunded"        value={summary ? fmt(summary.total_refunded, summary.currency) : '—'} sub={`${summary?.count_refunded ?? 0} transactions`} />
        <SummaryCard icon={XCircle}     color="text-red-400"     label="Failed"          value={summary?.count_failed ?? '—'} sub="payments" />
        <SummaryCard icon={Clock}       color="text-cream/30"    label="Total"           value={summary?.count_total ?? '—'} sub="all events" />
      </div>

      {/* 30-day Revenue Chart */}
      {(dailyRevenue.length > 0 || chartLoading) && (
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-4 mb-6" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart2 size={13} className="text-gold/50" />
              <span className="text-cream/30 text-[10px] uppercase tracking-wider">30-day Revenue</span>
            </div>
            {!chartLoading && dailyRevenue.length > 0 && (
              <span className="text-cream/20 text-[10px]">
                Total: {fmt(dailyRevenue.reduce((s, d) => s + d.revenue_pence, 0), 'gbp')}
              </span>
            )}
          </div>
          {chartLoading ? (
            <div className="h-28 flex items-center justify-center">
              <RefreshCw size={16} className="text-cream/10 animate-spin" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={96}>
              <BarChart data={dailyRevenue} margin={{ top: 0, right: 0, bottom: 0, left: 0 }} barSize={6}>
                <XAxis
                  dataKey="date"
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis hide />
                <Tooltip
                  cursor={{ fill: 'rgba(201,147,22,0.06)' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-[#06111f] border border-gold/20 rounded-lg px-3 py-2 text-xs shadow-xl">
                        <p className="text-cream/50">{new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                        <p className="text-gold font-medium">{fmt(d.revenue_pence, 'gbp')}</p>
                        {d.count > 0 && <p className="text-cream/25">{d.count} payment{d.count !== 1 ? 's' : ''}</p>}
                      </div>
                    );
                  }}
                />
                <Bar dataKey="revenue_pence" radius={[3, 3, 0, 0]}>
                  {dailyRevenue.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.revenue_pence > 0 ? '#C99316' : 'rgba(250,248,243,0.04)'}
                      fillOpacity={entry.revenue_pence > 0 ? 0.8 : 1}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/20" />
          <input
            type="text"
            placeholder="Search email, name, Stripe ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="payment-search"
            className="w-full bg-navy border border-gold/[0.06] text-cream/60 text-xs pl-8 pr-3 py-2 rounded-lg placeholder-cream/15 focus:outline-none focus:border-gold/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="status-filter"
          className="bg-navy border border-gold/[0.06] text-cream/50 text-xs px-3 py-2 rounded-lg focus:outline-none"
        >
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          data-testid="source-filter"
          className="bg-navy border border-gold/[0.06] text-cream/50 text-xs px-3 py-2 rounded-lg focus:outline-none"
        >
          {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Transaction table */}
      <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gold/[0.04]">
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">Date</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">Customer</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15 hidden md:table-cell">Product</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15">Amount</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15 hidden sm:table-cell">Status</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15 hidden lg:table-cell">Source</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-cream/15 hidden xl:table-cell">Charge ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-cream/20 text-xs">Loading transactions…</td></tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <CreditCard size={28} className="mx-auto text-cream/10 mb-3" />
                    <p className="text-cream/30 text-sm">No transactions found for this period.</p>
                    <p className="text-cream/15 text-xs mt-1">Try "All time" or use the Sync button to import from Stripe.</p>
                  </td>
                </tr>
              ) : transactions.map(t => (
                <tr
                  key={t.id}
                  data-testid={`txn-row-${t.stripe_charge_id || t.id}`}
                  onClick={() => setSelectedTxn(t)}
                  className="border-b border-gold/[0.03] hover:bg-cream/[0.015] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-cream/30 text-xs whitespace-nowrap">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    <p className="text-cream/15 text-[10px]">{t.created_at ? new Date(t.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-cream/70 text-xs">{t.customer_name || '—'}</p>
                    <p className="text-cream/25 text-[10px]">{t.customer_email || ''}</p>
                  </td>
                  <td className="px-4 py-3 text-cream/50 text-xs hidden md:table-cell max-w-[180px] truncate">{t.product_name || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-cream/80 text-xs font-medium tabular-nums">{fmt(t.amount, t.currency)}</p>
                    {t.amount_refunded > 0 && (
                      <p className="text-amber-400/60 text-[10px]">−{fmt(t.amount_refunded, t.currency)}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3 hidden lg:table-cell"><SourceBadge source={t.source} /></td>
                  <td className="px-4 py-3 text-cream/15 text-[10px] font-mono hidden xl:table-cell">{(t.stripe_charge_id || '').slice(-12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gold/[0.04] flex items-center justify-between">
            <span className="text-cream/20 text-xs">{total} transactions</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded text-cream/30 hover:text-cream/60 disabled:opacity-20 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-cream/30 text-xs px-2">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded text-cream/30 hover:text-cream/60 disabled:opacity-20 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedTxn && (
        <DetailPanel
          txn={selectedTxn}
          token={token}
          onClose={() => setSelectedTxn(null)}
          onRefundSuccess={(result) => { setRefundSuccess(result); fetchData(); }}
        />
      )}
    </div>
  );
};

export default AdminPayments;
